import * as Sentry from '@sentry/react';

// Google Translate and the browsers' built-in translators wrap page text in <font> elements.
// React still holds the original text nodes, so its next removeChild/insertBefore names a node
// whose parent is now the <font>, the DOM throws NotFoundError, and the error boundary replaces
// the whole app (PP-10172: residents translating onboarding crashed on the confirm step).
// Blocking translation would lock out residents who need it, so these two calls tolerate a node
// the translator moved instead. See https://github.com/facebook/react/issues/11538.

const reportMovedNode = (method: string) =>
  Sentry.addBreadcrumb({
    category: 'dom',
    level: 'warning',
    message: `${method} skipped a node moved outside React, likely by a page translator`,
  });

/* eslint-disable no-extend-native -- the fix has to sit under React's own DOM calls */
const originalRemoveChild = Node.prototype.removeChild;
Node.prototype.removeChild = function removeChild<T extends Node>(
  this: Node,
  child: T,
): T {
  if (child.parentNode !== this) {
    // The node is already out of this parent; leaving it where the translator put it is harmless.
    reportMovedNode('removeChild');
    return child;
  }
  return originalRemoveChild.call(this, child) as T;
};

const originalInsertBefore = Node.prototype.insertBefore;
Node.prototype.insertBefore = function insertBefore<T extends Node>(
  this: Node,
  newNode: T,
  referenceNode: Node | null,
): T {
  if (referenceNode && referenceNode.parentNode !== this) {
    // Append rather than drop: content in a slightly wrong spot beats content that never shows.
    reportMovedNode('insertBefore');
    return originalInsertBefore.call(this, newNode, null) as T;
  }
  return originalInsertBefore.call(this, newNode, referenceNode) as T;
};
/* eslint-enable no-extend-native */
